import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Badge, 
  IconButton, 
  Menu, 
  MenuItem, 
  Avatar, 
  Tooltip,
  Box,
  Typography,
  Divider,
  Fade,
  Chip
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import Auth from '../../pages/Auth';
import { useCart } from '../../context/CartContext';
import './Navbar.css';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#C6A43F',
    color: '#0A0F1E',
    fontWeight: 600,
    fontSize: '0.7rem',
    minWidth: '18px',
    height: '18px',
  },
}));

const Navbar = () => {
  const navigate = useNavigate();
  const { getCartCount, orders, wishlist } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    setCartCount(getCartCount());
  }, [getCartCount]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleProfileClick = (event) => {
    if (isLoggedIn) {
      setAnchorEl(event.currentTarget);
    } else {
      setAuthMode('login');
      setAuthOpen(true);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUser(null);
    handleClose();
    // Clear any user data from localStorage if needed
    localStorage.removeItem('mirae_user');
  };

  const handleNavigation = (path) => {
    navigate(path);
    handleClose();
    setMenuOpen(false);
  };

  const handleLoginSuccess = (userData) => {
    setIsLoggedIn(true);
    setUser(userData);
    setAuthOpen(false);
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
        <div className="navbar-container">
          {/* Logo */}
          <div className="navbar-logo">
            <Link to="/">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <h1>MIRAE</h1>
                <span className="logo-subtitle">Future of Brilliance</span>
              </motion.div>
            </Link>
          </div>

          {/* Desktop Navigation Links */}
          <div className={`navbar-links ${menuOpen ? 'active' : ''}`}>
            <Link to="/" className="nav-link" onClick={() => setMenuOpen(false)}>Home</Link>
            <Link to="/shop" className="nav-link" onClick={() => setMenuOpen(false)}>Collection</Link>
            <Link to="/shop?category=gold" className="nav-link" onClick={() => setMenuOpen(false)}>Gold</Link>
            <Link to="/shop?category=silver" className="nav-link" onClick={() => setMenuOpen(false)}>Silver</Link>
            <Link to="/shop?category=diamond" className="nav-link" onClick={() => setMenuOpen(false)}>Diamonds</Link>
            
            {/* Order History Link for Mobile */}
            {isLoggedIn && (
              <Link to="/orders" className="nav-link mobile-only" onClick={() => setMenuOpen(false)}>
                <HistoryIcon sx={{ mr: 1, fontSize: 18 }} />
                My Orders
                {orders?.length > 0 && (
                  <Chip 
                    label={orders.length} 
                    size="small" 
                    sx={{ 
                      ml: 1, 
                      bgcolor: '#C6A43F', 
                      color: '#0A0F1E',
                      height: '20px',
                      '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
                    }} 
                  />
                )}
              </Link>
            )}
            
            {/* Wishlist Link for Mobile */}
            {isLoggedIn && (
              <Link to="/wishlist" className="nav-link mobile-only" onClick={() => setMenuOpen(false)}>
                <FavoriteIcon sx={{ mr: 1, fontSize: 18 }} />
                Wishlist
                {wishlist?.length > 0 && (
                  <Chip 
                    label={wishlist.length} 
                    size="small" 
                    sx={{ 
                      ml: 1, 
                      bgcolor: '#C6A43F', 
                      color: '#0A0F1E',
                      height: '20px',
                      '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
                    }} 
                  />
                )}
              </Link>
            )}
          </div>

          {/* Action Icons */}
          <div className="navbar-actions">
            <button 
              className={`menu-toggle ${menuOpen ? 'active' : ''}`} 
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
            
            {/* Order History Icon (Desktop) */}
            {isLoggedIn && (
              <Tooltip title="My Orders">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconButton 
                    className="action-btn"
                    onClick={() => navigate('/orders')}
                    sx={{ position: 'relative' }}
                  >
                    <HistoryIcon sx={{ color: '#FFFFFF' }} />
                    {orders?.length > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: '#C6A43F',
                          color: '#0A0F1E',
                          fontSize: '0.6rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700
                        }}
                      >
                        {orders.length}
                      </Box>
                    )}
                  </IconButton>
                </motion.div>
              </Tooltip>
            )}

            {/* Wishlist Icon (Desktop) */}
            {isLoggedIn && (
              <Tooltip title="Wishlist">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconButton 
                    className="action-btn"
                    onClick={() => navigate('/wishlist')}
                    sx={{ position: 'relative' }}
                  >
                    <FavoriteIcon sx={{ color: '#FFFFFF' }} />
                    {wishlist?.length > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: '#C6A43F',
                          color: '#0A0F1E',
                          fontSize: '0.6rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700
                        }}
                      >
                        {wishlist.length}
                      </Box>
                    )}
                  </IconButton>
                </motion.div>
              </Tooltip>
            )}

            {/* Profile/Auth Button */}
            <Tooltip title={isLoggedIn ? "Profile" : "Login / Sign Up"}>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <IconButton className="profile-btn" onClick={handleProfileClick}>
                  {isLoggedIn ? (
                    <Avatar sx={{ 
                      width: 35, 
                      height: 35, 
                      bgcolor: '#C6A43F',
                      color: '#0A0F1E',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}>
                      {user?.name?.charAt(0) || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </Avatar>
                  ) : (
                    <PersonIcon sx={{ color: '#FFFFFF' }} />
                  )}
                </IconButton>
              </motion.div>
            </Tooltip>

            {/* Cart Button */}
            <Tooltip title="Shopping Cart">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <IconButton 
                  className="cart-btn"
                  onClick={() => navigate('/cart')}
                >
                  <StyledBadge badgeContent={cartCount}>
                    <ShoppingBagIcon sx={{ color: '#FFFFFF' }} />
                  </StyledBadge>
                </IconButton>
              </motion.div>
            </Tooltip>
          </div>
        </div>
      </nav>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 220,
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(198,164,63,0.2)',
            overflow: 'visible',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -8,
              right: 14,
              width: 16,
              height: 16,
              backgroundColor: 'inherit',
              transform: 'rotate(45deg)',
              borderLeft: '1px solid rgba(198,164,63,0.2)',
              borderTop: '1px solid rgba(198,164,63,0.2)',
            },
          },
        }}
      >
        {/* User Info */}
        {isLoggedIn && (
          <Box sx={{ px: 2, py: 1.5, bgcolor: alpha('#C6A43F', 0.05) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.name || 'Welcome Guest'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email || 'Sign in to view your account'}
            </Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 1 }} />
        
        {isLoggedIn ? (
          <>
            <MenuItem onClick={() => handleNavigation('/profile')} sx={{ py: 1.5 }}>
              <PersonIcon sx={{ mr: 2, fontSize: 20, color: '#C6A43F' }} />
              My Profile
            </MenuItem>
            
            <MenuItem onClick={() => handleNavigation('/orders')} sx={{ py: 1.5 }}>
              <HistoryIcon sx={{ mr: 2, fontSize: 20, color: '#C6A43F' }} />
              My Orders
              {orders?.length > 0 && (
                <Chip 
                  label={orders.length} 
                  size="small" 
                  sx={{ 
                    ml: 'auto', 
                    bgcolor: alpha('#C6A43F', 0.1), 
                    color: '#C6A43F',
                    height: '20px',
                    '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
                  }}
                />
              )}
            </MenuItem>
            
            <MenuItem onClick={() => handleNavigation('/wishlist')} sx={{ py: 1.5 }}>
              <FavoriteIcon sx={{ mr: 2, fontSize: 20, color: '#C6A43F' }} />
              Wishlist
              {wishlist?.length > 0 && (
                <Chip 
                  label={wishlist.length} 
                  size="small" 
                  sx={{ 
                    ml: 'auto', 
                    bgcolor: alpha('#C6A43F', 0.1), 
                    color: '#C6A43F',
                    height: '20px',
                    '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
                  }}
                />
              )}
            </MenuItem>
            
            {user?.role === 'admin' && (
              <MenuItem onClick={() => handleNavigation('/admin')} sx={{ py: 1.5 }}>
                <DashboardIcon sx={{ mr: 2, fontSize: 20, color: '#C6A43F' }} />
                Admin Dashboard
              </MenuItem>
            )}
            
            <Divider sx={{ my: 1 }} />
            
            <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: '#f44336' }}>
              <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
              Logout
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => {
              handleClose();
              setAuthMode('login');
              setAuthOpen(true);
            }} sx={{ py: 1.5 }}>
              <PersonIcon sx={{ mr: 2, fontSize: 20, color: '#C6A43F' }} />
              Login
            </MenuItem>
            
            <MenuItem onClick={() => {
              handleClose();
              setAuthMode('signup');
              setAuthOpen(true);
            }} sx={{ py: 1.5 }}>
              <PersonIcon sx={{ mr: 2, fontSize: 20, color: '#C6A43F' }} />
              Create Account
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Auth Modal */}
      <Auth 
        open={authOpen} 
        onClose={() => setAuthOpen(false)} 
        initialMode={authMode}
        onLogin={handleLoginSuccess}
      />
    </>
  );
};

export default Navbar;